import {
    FilterKey,
    FilterOptionsByKey,
    SelectedRoadmapFeature,
    SortCriteria,
    SortDirection,
    SortKey
} from "../types";

import { useRoadmapFeaturesContext } from "./context";
import {
    getFilteredAndSortedRoadmapFeatures,
    getNormalizedFeature,
    getNormalizedFeatures
} from "./transform";
import { setQueryParam, setSlug, clearSlug, setQueryParams } from "./url";

export const useFilteredAndSortedRoadmapFeatures = () => {
    const { features, filters, sortCriteria } = useRoadmapFeaturesContext();
    if (!features) {
        return null;
    }

    const filteredAndSortedFeatures = getFilteredAndSortedRoadmapFeatures({
        features,
        filters,
        sortCriteria
    });

    return getNormalizedFeatures(filteredAndSortedFeatures);
};

export const useSetFilter = () => {
    const { setFilters } = useRoadmapFeaturesContext();
    return function setFilter(filterKey: FilterKey, values: string[]) {
        setQueryParam(filterKey, values);
        setFilters((prev) => ({ ...prev, [filterKey]: values }));
    };
};

export const useClearFilter = () => {
    const { setFilters } = useRoadmapFeaturesContext();
    return function clearFilter(filterKey: FilterKey) {
        setQueryParam(filterKey, []);
        setFilters((prev) => ({
            ...prev,
            [filterKey]: []
        }));
    };
};

export const useFilter = (filterKey: FilterKey) => {
    const { filters } = useRoadmapFeaturesContext();
    return filters[filterKey];
};

export const useSetSearch = () => {
    const setFilter = useSetFilter();
    return function setSearch(value: string) {
        setFilter("search", [value]);
    };
};

export const useSearch = () => {
    const { filters } = useRoadmapFeaturesContext();
    return filters.search[0];
};

export const useSetSelectedFeature = () => {
    const ctx = useRoadmapFeaturesContext();
    return function setSelectedFeature(feature: SelectedRoadmapFeature) {
        setQueryParam("p", []);

        if (feature.pid) {
            setQueryParam("p", [feature.pid]);
        }
        setSlug(feature.slug, { basePath: ctx.pageBasePath });
        ctx.setSelectedFeature(feature);
    };
};
export const useSelectedFeature = () =>
    useRoadmapFeaturesContext().selectedFeature;

export const useClearSelectedFeature = () => {
    const ctx = useRoadmapFeaturesContext();
    return function clearSelectedFeature() {
        setQueryParam("p", []);
        clearSlug({ basePath: ctx.pageBasePath });
        ctx.setSelectedFeature(undefined);
    };
};

export const useSelectedFeatureData = () => {
    const { features } = useRoadmapFeaturesContext();
    const selectedFeature = useSelectedFeature();
    if (!features || !selectedFeature) {
        return undefined;
    }
    const feature = features.find(
        (feature) => feature.slug === selectedFeature.slug
    );
    if (!feature) {
        return undefined;
    }
    return getNormalizedFeature(feature);
};

export const useSetSortCriteria = () => {
    const ctx = useRoadmapFeaturesContext();
    return function setSortCriteria(sortCriteria: SortCriteria | undefined) {
        if (sortCriteria === undefined) {
            ctx.setSortCriteria(undefined);
            setQueryParams({
                sortBy: [],
                sort: []
            });
            return;
        }

        const { key, direction } = sortCriteria;
        ctx.setSortCriteria({ key, direction });
        setQueryParams({
            sortBy: [key],
            sort: [direction]
        });
    };
};

export interface UseSortResult {
    sortDirection?: SortDirection;
    onClick?: () => void;
}
export const useSort = (key: SortKey): UseSortResult => {
    const ctx = useRoadmapFeaturesContext();
    const setSortCriteria = useSetSortCriteria();
    if (!ctx.sortCriteria || ctx.sortCriteria.key !== key) {
        return {
            sortDirection: undefined,
            onClick: () => {
                setSortCriteria({ key, direction: "asc" });
            }
        };
    }

    const sortDirection = ctx.sortCriteria.direction;
    return {
        sortDirection: ctx.sortCriteria.direction,
        onClick: () => {
            if (sortDirection === undefined) {
                setSortCriteria({ key, direction: "asc" });
            }
            if (sortDirection === "asc") {
                setSortCriteria({ key, direction: "desc" });
            }
            if (sortDirection === "desc") {
                setSortCriteria(undefined);
            }
        }
    };
};

export const useFilterOptions = (filterKey: keyof FilterOptionsByKey) => {
    const { filterOptionsByKey } = useRoadmapFeaturesContext();
    return filterOptionsByKey[filterKey].sort((a, b) =>
        a.label > b.label ? 1 : -1
    );
};

export const useResetAll = () => {
    const setFilter = useSetFilter();
    const setSortCriteria = useSetSortCriteria();

    return function reset() {
        setSortCriteria(undefined);
        setFilter("search", []);
        setFilter("status", []);
        setFilter("category", []);
        setFilter("selectedProduct", []);
    };
};
